// src/components/CustomerFlow/Steps/PhoneValidationStep.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faCheckCircle, faExclamationTriangle, faEdit } from '@fortawesome/free-solid-svg-icons';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import api from '../../../services/api';
import Swal from 'sweetalert2';
import { PhoneField } from '../../ui';

const formatPhoneForDisplay = (e164) => {
  if (!e164 || typeof e164 !== 'string') return '';
  try {
    const parsed = parsePhoneNumberFromString(e164.trim());
    return parsed ? parsed.formatInternational() : e164;
  } catch {
    return e164;
  }
};

const INITIAL_PHONE_PAYLOAD = {
  e164: null,
  countryIso2: null,
  national: null,
  isPossible: false,
  isValid: false,
  type: 'UNKNOWN',
  rawInput: ''
};

const allowPossibleButInvalid = process.env.REACT_APP_PHONE_ALLOW_POSSIBLE_WARNING === 'true';

const MAX_VERIFY_ATTEMPTS = 3;   // intentos fallidos de código por ciclo
const MAX_PHONE_CHANGES = 1;     // cuántas veces puede cambiar el teléfono
const OTP_LENGTH = 6;

const normalizeOtpCode = (value = '') => String(value).replace(/\D/g, '').slice(0, OTP_LENGTH);

const PhoneValidationStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  // Estados existentes
  const [isLoading, setIsLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showEditPhone, setShowEditPhone] = useState(false);
  const [phonePayload, setPhonePayload] = useState(INITIAL_PHONE_PAYLOAD);
  const [hasPhoneBlurred, setHasPhoneBlurred] = useState(false);
  const [hasPhoneSubmitAttempt, setHasPhoneSubmitAttempt] = useState(false);
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  
  // Nuevos estados para el flujo de verificación
  const [verificationStatus, setVerificationStatus] = useState(null); // 'verified' | 'pending_code' | 'should_send' | 'loading'
  const [phoneId, setPhoneId] = useState(null);
  const [purchaseRequestUid, setPurchaseRequestUid] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [sendAttempts, setSendAttempts] = useState(0); // Intentos de envío (máx 3) - solo para Reenviar SMS
  const [phoneChangeCount, setPhoneChangeCount] = useState(0); // veces que cambió el teléfono (máx 1)
  const [verifyAttempts, setVerifyAttempts] = useState(0); // Intentos de verificación (máx 3 por ciclo)
  const [isSending, setIsSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendError, setSendError] = useState(null);
  const otpInputRef = useRef(null);
  const webOtpAbortRef = useRef(null);
  const autoSubmittedOtpRef = useRef('');

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['PhoneValidationStep'];
    
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      
      onNext({
        step_name: 'PhoneValidationStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Verificar estado del teléfono al montar el componente
  useEffect(() => {
    // Verificar que flowData existe y tiene las propiedades necesarias
    if (flowData?.tokenCustomerId && flowData?.tokenPurchaseRequestId) {
      checkPhoneStatus();
    } else {
      // Si no hay datos, deshabilitar loading y mostrar error
      console.warn('PhoneValidationStep: flowData incompleto:', flowData);
      setIsLoading(false);
      setValidationError('No se pudo obtener la información de la solicitud. Por favor, recarga la página.');
      // Establecer un estado por defecto para mostrar algo
      setVerificationStatus('should_send');
    }
  }, [flowData]);

  const checkPhoneStatus = async () => {
    try {
      setIsLoading(true);
      setValidationError(null);
      
      // Primero obtener el UID de la purchase request usando el ID
      // Necesitamos obtener el purchase request para tener el UID
      let purchaseRequestUid = null;
      
      try {
        // Intentar obtener el purchase request para obtener el UID
        const purchaseRequestResponse = await api.get(
          `/customers/${flowData.tokenCustomerId}/purchase-requests/${flowData.tokenPurchaseRequestId}`
        );
        
        if (purchaseRequestResponse.data?.uid) {
          purchaseRequestUid = purchaseRequestResponse.data.uid;
        } else if (purchaseRequestResponse.data?.data?.uid) {
          purchaseRequestUid = purchaseRequestResponse.data.data.uid;
        }
      } catch (err) {
        console.warn('PhoneValidationStep: No se pudo obtener el purchase request, intentando con ID directamente');
        // Si falla, intentar usar el ID como UID (puede que sea el mismo)
        purchaseRequestUid = flowData.tokenPurchaseRequestId;
      }
      
      if (!purchaseRequestUid) {
        console.error('PhoneValidationStep: No se encontró purchaseRequestUid');
        setValidationError('No se pudo obtener la información de la solicitud.');
        setVerificationStatus('should_send');
        setIsLoading(false);
        return;
      }
      
      console.log('PhoneValidationStep: Llamando check-status con purchase_request_uid:', purchaseRequestUid);
      
      const response = await api.get('/phone-verification/check-status', {
        params: { purchase_request_uid: purchaseRequestUid }
      });
      
      console.log('PhoneValidationStep: Respuesta check-status:', response.data);
      
      const data = response.data;
      setPhoneId(data.phone_id);
      setPhoneNumber(data.phone || '');
      setPurchaseRequestUid(purchaseRequestUid);
      setCodeExpiresAt(data.expires_at);
      
      // Determinar estado
      // PRIORIDAD 1: Si el teléfono ya está verificado, completar el paso inmediatamente
      // No importa si hay un código pendiente (has_pending_code), si ya está verificado, no se necesita nada más
      if (data.is_verified) {
        // Teléfono ya verificado - avanzar automáticamente sin enviar SMS
        onNext({ 
          step_name: 'PhoneValidationStep',
          step_status: 'completed',
          phone_validated: true,
          phone_id: data.phone_id,
          verification_timestamp: new Date().toISOString()
        }, 'completed');
        return;
      }
      
      // PRIORIDAD 2: Solo si NO está verificado, verificar si requiere entrada de código
      // requires_code_entry tiene prioridad sobre has_pending_code porque es más específico
      if (data.requires_code_entry) {
        // Mostrar campo de código - el usuario debe ingresar el código
        setVerificationStatus('pending_code');
        setCodeSent(true);
      } else if (data.has_pending_code && !data.is_verified) {
        // Hay un código pendiente pero el teléfono no está verificado aún
        // Mostrar campo de código para que el usuario lo ingrese
        setVerificationStatus('pending_code');
        setCodeSent(true);
      } else if (data.should_request_code) {
        // Mostrar botón "Enviar código" - el usuario debe solicitar un nuevo código
        setVerificationStatus('should_send');
      } else {
        // Estado por defecto si no hay ninguna condición específica
        setVerificationStatus('should_send');
      }
      
    } catch (error) {
      console.error('Error checking phone status:', error);
      
      // Manejar específicamente el caso 404 - teléfono no encontrado
      if (error.response?.status === 404) {
        // Mostrar mensaje del servidor (ui_message o message)
        const errorMsg = error.response?.data?.ui_message || error.response?.data?.message || 'No hay teléfono registrado';
        setValidationError(errorMsg);
        
        // Activar formulario de ingreso de teléfono
        setShowEditPhone(true);
        
        // NO establecer verificationStatus para que no se muestre el botón de enviar
        setVerificationStatus(null);
      } else {
        // Para otros errores, mostrar mensaje y solo permitir envío si hay un número
        setValidationError(error.response?.data?.message || 'Error al verificar el estado del teléfono. Por favor, intenta nuevamente.');
        
        // Solo habilitar botón de envío si hay un número de teléfono válido
        if (phoneNumber && phoneNumber.trim() !== '') {
          setVerificationStatus('should_send');
        } else {
          setVerificationStatus(null);
          setShowEditPhone(true);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (sendAttempts >= 3) {
      setValidationError('Has alcanzado el máximo de intentos de envío. Por favor contacta al soporte.');
      return;
    }
    
    try {
      setIsSending(true);
      setSendError(null);
      setValidationError(null);
      
      const response = await api.post('/phone-verification/send', {
        phone_id: phoneId,
        purchase_request_uid: purchaseRequestUid
      });
      
      if (response.status === 200) {
        setCodeSent(true);
        setVerificationStatus('pending_code');
        setSendAttempts(prev => prev + 1);
        setVerifyAttempts(0); // Reset verification attempts
        setOtpCode('');
        autoSubmittedOtpRef.current = '';
      }
      
    } catch (error) {
      console.error('Error sending code:', error);
      const newSendAttempts = sendAttempts + 1;
      setSendAttempts(newSendAttempts);
      
      const errorMsg = error.response?.data?.message || 'Error al enviar el código. Intenta nuevamente.';
      
      if (newSendAttempts >= 3) {
        setSendError('Has alcanzado el máximo de intentos de envío.');
        setShowEditPhone(true); // Permitir cambiar número
      } else {
        setSendError(errorMsg);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = normalizeOtpCode(e.target.value);
    setOtpCode(value);
    setValidationError(null);
  };

  const handleOtpPaste = (e) => {
    const pastedValue = normalizeOtpCode(e.clipboardData?.getData('text') || '');

    if (!pastedValue) {
      return;
    }

    e.preventDefault();
    setOtpCode(pastedValue);
    setValidationError(null);
  };

  const handleValidateOtp = useCallback(async (codeToValidate = otpCode) => {
    const normalizedCode = normalizeOtpCode(codeToValidate);

    if (normalizedCode.length !== OTP_LENGTH) {
      setValidationError('Por favor, ingresa el código de 6 dígitos');
      return;
    }
    
    // Verificar intentos de verificación (máx 3 por envío)
    if (verifyAttempts >= 3) {
      setValidationError('Has alcanzado el máximo de intentos. Puedes reenviar el código.');
      setShowEditPhone(true);
      return;
    }
    
    try {
      setIsValidating(true);
      setValidationError(null);
      
      const response = await api.post('/phone-verification/verify', {
        phone_id: phoneId,
        code: normalizedCode,
        purchase_request_uid: purchaseRequestUid
      });
      
      if (response.status === 200) {
        // Verificación exitosa
        onNext({ 
          step_name: 'PhoneValidationStep',
          step_status: 'completed',
          phone_validated: true,
          phone_id: phoneId,
          verification_timestamp: new Date().toISOString()
        }, 'completed');
      }
      
    } catch (error) {
      console.error('Error validating OTP:', error);
      
      const newVerifyAttempts = verifyAttempts + 1;
      setVerifyAttempts(newVerifyAttempts);
      
      const errorMessage = error.response?.data?.ui_message 
        || error.response?.data?.message 
        || 'Código inválido. Por favor, intenta nuevamente.';
      
      if (newVerifyAttempts >= 3) {
        setValidationError('Has alcanzado el máximo de intentos de verificación. Puedes reenviar el código.');
        setShowEditPhone(true);
      } else {
        setValidationError(errorMessage);
      }
      
      setOtpCode(''); // Limpiar campo
      autoSubmittedOtpRef.current = '';
      
    } finally {
      setIsValidating(false);
    }
  }, [onNext, otpCode, phoneId, purchaseRequestUid, verifyAttempts]);

  const phoneValidationError = useMemo(() => {
    const strictValidationEnabled = hasPhoneBlurred || hasPhoneSubmitAttempt;
    if (!strictValidationEnabled) return '';

    const hasInput = Boolean(phonePayload.rawInput?.trim() || phonePayload.e164);
    if (!hasInput) return 'Ingresa un número de teléfono';
    if (!phonePayload.e164) return 'No pudimos interpretar el número. Revisa país y formato.';
    if (!phonePayload.isValid) {
      if (allowPossibleButInvalid && phonePayload.isPossible) return '';
      return 'El número no es válido para el país seleccionado';
    }
    return '';
  }, [phonePayload, hasPhoneBlurred, hasPhoneSubmitAttempt, allowPossibleButInvalid]);

  const isPhonePayloadValid = Boolean(
    phonePayload.e164 &&
    (phonePayload.isValid || (allowPossibleButInvalid && phonePayload.isPossible))
  );

  const handlePhoneChange = (payload) => {
    setPhonePayload(payload);
  };

  const handlePhoneBlur = (payload) => {
    setHasPhoneBlurred(true);
    if (payload) setPhonePayload(payload);
  };

  const handleUpdatePhone = async () => {
    setHasPhoneSubmitAttempt(true);
    if (phoneValidationError || !isPhonePayloadValid) return;

    const e164Phone = phonePayload.e164;

    try {
      setIsUpdatingPhone(true);
      setValidationError(null);
      
      // Usar el endpoint de retry existente (E.164 canónico)
      const response = await api.post(
        `/customers/${flowData.tokenCustomerId}/purchase-requests/${flowData.tokenPurchaseRequestId}/self-service/phone-validation/retry`,
        {
          new_phone_number: e164Phone
        }
      );
      
      if (response.data.success) {
        const newPhoneFromRetry = response.data?.data?.phone_number;
        setShowEditPhone(false);
        setPhonePayload(INITIAL_PHONE_PAYLOAD);
        setHasPhoneBlurred(false);
        setHasPhoneSubmitAttempt(false);
        setOtpCode('');
        setVerifyAttempts(0);
        setPhoneChangeCount(prev => prev + 1);
        setSendAttempts(0); // Reset para dar 3 reenvíos al nuevo número

        await checkPhoneStatus();
        if (newPhoneFromRetry) {
          setPhoneNumber(newPhoneFromRetry);
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Número actualizado',
          text: 'Se ha enviado un nuevo código a tu nuevo número.',
          confirmButtonText: 'Entendido'
        });
      } else {
        throw new Error(response.data.message || 'No se pudo actualizar el número de teléfono');
      }
      
    } catch (error) {
      console.error('Error updating phone number:', error);
      setValidationError(error.response?.data?.message || 'No se pudo actualizar el número de teléfono. Por favor, intenta nuevamente.');
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showEditPhone) {
        handleUpdatePhone();
      } else {
        handleValidateOtp();
      }
    }
  };

  useEffect(() => {
    if (verificationStatus !== 'pending_code' || showEditPhone) {
      return undefined;
    }

    const focusTimeout = window.setTimeout(() => {
      otpInputRef.current?.focus();
      otpInputRef.current?.select();
    }, 50);

    return () => window.clearTimeout(focusTimeout);
  }, [verificationStatus, showEditPhone]);

  useEffect(() => {
    if (verificationStatus !== 'pending_code' || showEditPhone || isValidating) {
      return undefined;
    }

    if (otpCode.length !== OTP_LENGTH) {
      autoSubmittedOtpRef.current = '';
      return undefined;
    }

    if (autoSubmittedOtpRef.current === otpCode) {
      return undefined;
    }

    autoSubmittedOtpRef.current = otpCode;
    handleValidateOtp(otpCode);

    return undefined;
  }, [handleValidateOtp, isValidating, otpCode, showEditPhone, verificationStatus]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return undefined;
    }

    if (
      verificationStatus !== 'pending_code' ||
      showEditPhone ||
      !window.isSecureContext ||
      !('OTPCredential' in window) ||
      !navigator.credentials?.get
    ) {
      return undefined;
    }

    const abortController = new AbortController();
    webOtpAbortRef.current?.abort();
    webOtpAbortRef.current = abortController;

    navigator.credentials.get({
      otp: { transport: ['sms'] },
      signal: abortController.signal,
    }).then((otpCredential) => {
      const receivedCode = normalizeOtpCode(otpCredential?.code || '');

      if (receivedCode.length === OTP_LENGTH) {
        setValidationError(null);
        setOtpCode(receivedCode);
      }
    }).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.debug('WebOTP unavailable or failed:', error);
      }
    });

    return () => {
      abortController.abort();
      if (webOtpAbortRef.current === abortController) {
        webOtpAbortRef.current = null;
      }
    };
  }, [showEditPhone, verificationStatus]);

  if (isLoading) {
    return (
      <div className="phone-validation-step bg-white p-4 rounded-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p>Cargando información de validación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="phone-validation-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3 className="phone-validation-step__title">Verifica tu teléfono</h3>
      </div>

      <div className="step-body">
        {!showEditPhone ? (
          <>
            {/* Mensaje de error si no hay verificationStatus */}
            {!verificationStatus && (
              <div className="alert alert-light-warning" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <p className="mb-0">
                  Cargando información de verificación...
                  {validationError && (
                    <span className="d-block mt-2 small">{validationError}</span>
                  )}
                </p>
              </div>
            )}

            {/* Vista: Should Send Code - Botón para enviar código */}
            {verificationStatus === 'should_send' && (
              <>
                {phoneNumber && phoneNumber.trim() !== '' ? (
                  <>
                    <p className="lead text-center mb-4">
                      Te enviaremos un código de verificación al
                    </p>
                    <p className="text-center mb-4">
                      <strong className="fs-5">{formatPhoneForDisplay(phoneNumber)}</strong>
                    </p>
                  </>
                ) : (
                  <div className="alert alert-light-warning mb-4" role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <p className="mb-0">
                      No hay número de teléfono registrado. Por favor, ingresa tu número para continuar.
                    </p>
                  </div>
                )}
                
                <button
                  className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full phone-validation-step__primary-btn"
                  onClick={handleSendCode}
                  disabled={isSending || !phoneNumber || phoneNumber.trim() === ''}
                >
                  {isSending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Enviando...
                    </>
                  ) : (
                    'Enviar código'
                  )}
                </button>
                
                {sendError && (
                  <div className="alert alert-light-danger" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {sendError}
                    {sendAttempts < 3 && (
                      <p className="mb-0 mt-2 small">
                        Intentos de envío: {sendAttempts} de 3
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Vista: Pending Code - Campo de código y validación */}
            {verificationStatus === 'pending_code' && (
              <>
                <p className="lead text-center mb-2">
                  Te enviamos un código SMS al
                </p>
                <p className="text-center mb-3">
                  <strong className="fs-5">{formatPhoneForDisplay(phoneNumber)}</strong>
                </p>

                {/* Error de validación general (cuando no hay 6 dígitos) */}
                {validationError && otpCode.length < 6 && (
                  <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {validationError}
                  </div>
                )}

                {/* Campo para ingresar código OTP */}
                <div className="mb-4 mt-5">
                  <label htmlFor="otp-code" className="form-label text-center d-block">
                    Código de verificación
                  </label>
                  <input
                    type="text"
                    id="otp-code"
                    ref={otpInputRef}
                    name="one-time-code"
                    className={`form-control form-control-lg text-center phone-validation-step__otp-input ${validationError && otpCode.length === OTP_LENGTH ? 'is-invalid' : ''}`}
                    value={otpCode}
                    onChange={handleOtpChange}
                    onPaste={handleOtpPaste}
                    onKeyPress={handleKeyPress}
                    placeholder="000000"
                    maxLength={OTP_LENGTH}
                    disabled={isValidating}
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    enterKeyHint="done"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-label="Código de verificación de 6 dígitos"
                    aria-describedby="otp-helper-text"
                  />
                  {!validationError && (
                    <small id="otp-helper-text" className="form-text text-muted d-block text-center mt-2">
                      El código vence en 120 minutos
                    </small>
                  )}
                  {validationError && otpCode.length === 6 && (
                    <div className="invalid-feedback d-block text-center">
                      {validationError}
                    </div>
                  )}
                </div>

                {/* Botón de validación */}
                <button
                  className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full phone-validation-step__primary-btn"
                  onClick={handleValidateOtp}
                  disabled={isValidating || otpCode.length !== 6}
                >
                  {isValidating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Validando...
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </button>

                {/* Acción secundaria - Reenviar código */}
                <div className="text-center">
                  <p className="text-muted small mb-0">
                    ¿No llegó el código?{' '}
                    <button 
                      className="rg-btn rg-btn--link phone-validation-step__link-btn"
                      onClick={handleSendCode}
                      disabled={isSending || sendAttempts >= 3}
                      style={{ verticalAlign: 'baseline' }}
                    >
                      Reenviar SMS
                    </button>
                  </p>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Vista: Cambiar número de teléfono */}
            {phoneChangeCount >= MAX_PHONE_CHANGES ? (
              <>
                {/* Máximo de intentos alcanzados - Contactar soporte */}
                <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2 d-block" />
                    <strong>Has alcanzado el máximo de intentos</strong>
                    <p className="mb-0 mt-2">
                      Por favor contacta al soporte para obtener ayuda.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Formulario para editar teléfono */}
                {/* Mostrar mensaje apropiado según el contexto */}
                {phoneChangeCount === 0 && verifyAttempts === 0 ? (
                  // Activado por 404 - No hay teléfono registrado
                  <div className="alert alert-light-info py-2 px-3 mb-3 d-flex align-items-center" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faPhone} className="me-2 fs-5" />
                    <div>
                      <div className="fw-semibold small mb-1">Ingresa tu número de teléfono</div>
                      <div className="small">
                        {validationError || 'Para continuar necesitamos verificar tu número de teléfono'}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Activado por intentos máximos alcanzados
                  !validationError || !validationError.includes('máximo de intentos') ? (
                    <div className="alert alert-light-warning py-2 px-3 mb-3 d-flex align-items-center" role="alert" aria-live="polite">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 fs-5" />
                      <div>
                        <div className="fw-semibold small mb-1">Máximo de intentos alcanzado</div>
                        <div className="small">Actualiza tu número para recibir un nuevo código</div>
                      </div>
                    </div>
                  ) : null
                )}

                {/* Error de validación en vista de edición - solo si no es el mensaje inicial de 404 */}
                {validationError && (phoneChangeCount > 0 || verifyAttempts > 0) && (
                  <div className="alert alert-light-danger mb-4" role="alert" aria-live="polite">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {validationError}
                  </div>
                )}

                <div
                  className="mb-4"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isPhonePayloadValid && !isUpdatingPhone) {
                      e.preventDefault();
                      handleUpdatePhone();
                    }
                  }}
                >
                  <PhoneField
                    id="new-phone"
                    name="new_phone_number"
                    label={phoneChangeCount === 0 && verifyAttempts === 0 ? 'Número de teléfono' : 'Nuevo número de teléfono'}
                    placeholder="Ej: 11 2345 6789 o 911 2345 6789"
                    defaultCountry="AR"
                    required
                    disabled={isUpdatingPhone}
                    error={phoneValidationError}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    autoFocus
                  />
                </div>

                <button
                  className="rg-btn rg-btn--primary rg-btn--lg rg-btn--full phone-validation-step__primary-btn"
                  onClick={handleUpdatePhone}
                  disabled={isUpdatingPhone || !isPhonePayloadValid}
                >
                  {isUpdatingPhone ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {phoneChangeCount === 0 && verifyAttempts === 0 ? 'Enviando código...' : 'Actualizando...'}
                    </>
                  ) : (
                    phoneChangeCount === 0 && verifyAttempts === 0 ? 'Continuar' : 'Actualizar y continuar'
                  )}
                </button>

                {/* Solo mostrar botón cancelar si no fue activado por 404 (hay estado previo) */}
                {(phoneChangeCount > 0 || verifyAttempts > 0) && (
                  <button
                    className="rg-btn rg-btn--link rg-btn--full phone-validation-step__cancel-btn"
                    onClick={() => {
                      setShowEditPhone(false);
                      setPhonePayload(INITIAL_PHONE_PAYLOAD);
                      setHasPhoneBlurred(false);
                      setHasPhoneSubmitAttempt(false);
                      setValidationError(null);
                    }}
                    disabled={isUpdatingPhone}
                  >
                    Cancelar
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneValidationStep;

